import type {
  ForgeCanvasPayload,
  CanvasNode,
  CanvasEdge,
  CanvasShape,
} from "@/store/useCanvasStore";
import type { WirePayload } from "./canvasWire";
import {
  mapNode,
  mapEdge,
  mapShape,
  toWireNode,
  toWireEdge,
  toWireShape,
} from "./canvasWire";
import { apiUrl } from "@/config/api";
import { tracedFetch } from "@/utils/tracedFetch";

export type GetToken = () => Promise<string | null>;

/** Sends the request and hands back the raw Response, whatever its status.
 *  Callers that need to read a specific non-2xx body use this directly. */
async function authedRequest(url: string, getToken: GetToken, init?: RequestInit): Promise<Response> {
  const token = await getToken();
  return tracedFetch(apiUrl(url), {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

async function authedFetch(url: string, getToken: GetToken, init?: RequestInit): Promise<Response> {
  const res = await authedRequest(url, getToken, init);
  if (!res.ok) {
    const body = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${body}`);
  }
  return res;
}

/**
 * A save refused because someone else wrote first. Distinct from a save
 * *failure*: nothing is wrong, the client is simply behind, and the fix is to
 * reconcile against `revision` and try again. Collapsed into a generic Error it
 * would be counted as a failure and surfaced to the user as one.
 */
export class CanvasConflictError extends Error {
  readonly revision: number;
  constructor(revision: number) {
    super(`canvas revision conflict: server is at ${revision}`);
    this.name = "CanvasConflictError";
    this.revision = revision;
  }
}

// ── Wire contract ─────────────────────────────────────────────────────────────
// Types and converters live in canvasWire.ts so the realtime path can share them
// without importing this module (which does I/O and is mocked in store tests).
// Re-exported here because callers already import them from the service.

export type {
  WireNode,
  WireEdge,
  WireShape,
  WirePayload,
} from "./canvasWire";

export { mapNode, mapEdge, mapShape } from "./canvasWire";

// ── Public API ────────────────────────────────────────────────────────────────

export async function fetchCanvas(forgeId: string, getToken: GetToken): Promise<ForgeCanvasPayload> {
  const res = await authedFetch(`/api/forges/${forgeId}/canvas`, getToken);
  const wire = (await res.json()) as WirePayload;
  return {
    nodes: (wire.nodes ?? []).map(mapNode),
    edges: (wire.edges ?? []).map(mapEdge),
    shapes: (wire.shapes ?? []).map(mapShape),
    // Older servers omit it. 0 is the right fallback: it is where every forge
    // starts, so the first save states a base the server will accept.
    revision: wire.revision ?? 0,
  };
}

/**
 * `knownIds` is the delete scope this client claims — the ids it has evidence
 * the server holds. The server prunes only within it, so a row this tab never
 * heard about (an MCP write, say) survives a save built on a stale snapshot.
 * Sending fewer ids is safe; sending ids the client never saw is the bug.
 */
export async function saveCanvas(
  forgeId: string,
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  shapes: CanvasShape[],
  knownIds: string[],
  baseRevision: number | null,
  getToken: GetToken,
): Promise<number> {
  const res = await authedRequest(`/api/forges/${forgeId}/canvas`, getToken, {
    method: "POST",
    body: JSON.stringify(savePayload(nodes, edges, shapes, knownIds, baseRevision)),
  });

  if (res.status === 409) {
    const body = (await res.json().catch(() => ({}))) as { revision?: number };
    throw new CanvasConflictError(body.revision ?? 0);
  }
  if (!res.ok) {
    const body = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${body}`);
  }

  const body = (await res.json().catch(() => ({}))) as { revision?: number };
  // A server that predates the revision field answers 204 with no body. Falling
  // back to the base keeps such a client sending a base the server ignores
  // anyway, rather than poisoning it with 0.
  return body.revision ?? baseRevision ?? 0;
}

/** The one place the save body is built, so the keepalive path cannot drift
 *  from the normal one — they have silently disagreed before. */
function savePayload(
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  shapes: CanvasShape[],
  knownIds: string[],
  baseRevision: number | null,
): Record<string, unknown> {
  return {
    nodes: nodes.map(toWireNode),
    edges: edges.map(toWireEdge),
    shapes: shapes.map(toWireShape),
    known_ids: knownIds,
    // Omitted rather than null when there is no base: the server reads absence
    // as "unchecked", and a null would decode to the same pointer anyway — but
    // only absence says so on the wire.
    ...(baseRevision !== null ? { base_revision: baseRevision } : {}),
  };
}

// Fire-and-forget save using a pre-fetched token + keepalive: true so the
// browser completes the request even after page unload (beforeunload handler).
// A conflict here is unrecoverable by construction — the page is going away and
// there is nobody left to reconcile. The unsaved canvas still reaches
// localStorage via flushCanvasPersistence, which is what the next load reads.
export function saveCanvasKeepalive(
  forgeId: string,
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  shapes: CanvasShape[],
  knownIds: string[],
  baseRevision: number | null,
  token: string,
): void {
  tracedFetch(apiUrl(`/api/forges/${forgeId}/canvas`), {
    method: "POST",
    keepalive: true,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(savePayload(nodes, edges, shapes, knownIds, baseRevision)),
  });
}
